import { LinkTo } from '@ember/routing';
import { hash } from '@ember/helper';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { or } from 'tio-ui/utilities';
import { tracked } from '@glimmer/tracking';
import Component from '@glimmer/component';
import Checkbox from 'tio-ui/components/checkbox';
import CreateFromDocument from 'tio-common/components/account-transactions/create-from-document';
import type { AccountExtension as AccountModel } from 'tio-data/data/resources/account.ext';
import type { MatchParticipantExtension as MatchParticipantModel } from 'tio-data/data/resources/match-participant.ext';
import type RouterService from '@ember/routing/router-service';

interface SyfPaymentsNewRouteSignature {
  Args: {
    // this template is rendered on a child route of syf without any intervening route
    // definitions so it shares a model with its eventual parent; convenient ember convention
    model: {
      matchParticipant: MatchParticipantModel;
      accounts: Array<AccountModel>;
    };
  };
  Blocks: {
    default: [];
  };
}

export default class SyfPaymentsNewRoute extends Component<SyfPaymentsNewRouteSignature> {
  @service declare router: RouterService;

  @tracked loanPaymentsConfirmed = false;

  get noLinkedAccounts() {
    return this.args.model.accounts.length === 0;
  }

  get accountsInRelink() {
    return this.args.model.accounts.some((account) => account.inRelink);
  }

  // Gate submission on the participant certifying the uploaded statement; the
  // shared component additionally requires a document and completed rows.
  get submitDisabled() {
    return !this.loanPaymentsConfirmed;
  }

  toggleLoanPaymentsConfirmed = () => {
    this.loanPaymentsConfirmed = !this.loanPaymentsConfirmed;
  };

  // see description in https://tuitionio.atlassian.net/browse/DEV-82; spec seems
  // like a back navigation
  navigateBack = () => {
    window.history.back();
  };

  goToDashboard = () => {
    this.router.transitionTo('authenticated.syf.dashboard');
  };

  <template>
    <h2 class="text-midnight tio-text-lg-semibold mt-4 mb-4">
      {{t "syf.payments.new.header"}}
    </h2>
    {{#if this.noLinkedAccounts}}
      <p class="mb-8">
        <LinkTo
          @route="authenticated.observability.upload"
          @query={{hash source="syf"}}
          class="tio-fg-info underline"
        >
          {{t "syf.payments.new.unlinked_anchor_text"}}
        </LinkTo>
        {{t "syf.payments.new.unlinked_info"}}
      </p>
    {{else if this.accountsInRelink}}
      <p class="mb-8">
        {{t "syf.payments.new.relink_prefix"}}
        <LinkTo
          @route="authenticated.observability.upload"
          @query={{hash source="syf"}}
          class="tio-fg-info underline"
        >
          {{t "syf.payments.new.relink_anchor_text"}}
        </LinkTo>
        {{t "syf.payments.new.relink_info"}}
      </p>
    {{else}}
      <p class="mb-8">{{t "syf.payments.new.linked_info"}}</p>
    {{/if}}

    <div class="max-w-screen-sm">
      <CreateFromDocument
        @person={{@model.matchParticipant.person}}
        @via="SELF_REPORTED"
        @submitDisabled={{this.submitDisabled}}
        @onComplete={{this.goToDashboard}}
        @onCancel={{this.navigateBack}}
      >
        <:beforeActions>
          <Checkbox
            data-test-id="certify-payments-checkbox"
            @onChange={{this.toggleLoanPaymentsConfirmed}}
            @checked={{this.loanPaymentsConfirmed}}
            class="cursor-pointer"
            @label={{or
              @model.matchParticipant.matchPlan.verifyAccountStatement
              (t "syf.shared.match_plan.default_verify_statement")
            }}
          />
        </:beforeActions>
      </CreateFromDocument>
    </div>
  </template>
}
